// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The notes the sequencer plays, and the score it plays them from (M3-28, 07 §5).
//
// "A step sequencer, **not a rendered track**." The distinction is the whole design. A track is
// a file; this product has no files. A sequencer is a program, and a program can be driven by
// the state of the game — which is what 07 §5's table asks for: the arp enters at Star Core 1,
// the drums at 10, the pad at 20, and the lead only in multiball and SUPERNOVA. Music that
// arrives because the player earned it is the difference between a soundtrack and a machine.
//
// **One buffer per timbre, pitched by playback rate.** Seven notes cover the whole score — bass,
// arp, pad, lead, kick, snare, hat — because a sampler is what a note buffer already is, and
// 07 §9's cap of 220 buffers has fourteen left in it after the mechanical layer and the speech.
// Rendering a buffer per pitch would have cost sixty and bought nothing a listener could name.
//
// **Band-limited wavetables, not naive ramps.** A saw built by counting from −1 to 1 has every
// harmonic there is, and the ones above Nyquist fold back as inharmonic noise that follows the
// pitch downwards — the sound of a cheap synth, and the one artefact that would make this layer
// obviously synthetic in a product whose whole claim is that generated audio need not be. One
// cycle is built additively, once, and read with a phase accumulator; the harmonic limit is set
// low enough that playing the table an octave up still lands under Nyquist.

import type { NoiseSource, Samples } from './dsp'
import {
  addPitchDrop,
  Biquad,
  decayCoefficient,
  decayCutoff,
  fadeOut,
  filterInPlace,
  normalise,
  samplesFor,
} from './dsp'

/** One cycle of a waveform, read by phase. */
export type Wavetable = Float64Array

/** Points in a cycle. 2048 is inaudibly finer than the ear at any pitch this score uses. */
const TABLE_POINTS = 2048

/**
 * Build one cycle additively.
 *
 * `topHz` is where the harmonics stop, at the pitch the table is *rendered* for. Everything the
 * sequencer plays is pitched by playback rate, so the limit has to leave room: a table played
 * an octave up doubles every harmonic with it, and content that was at 11 kHz arrives at 22 and
 * aliases. Half of Nyquist at the render pitch is the rule, and it is why the tables here stop
 * where they do rather than at the edge of hearing.
 */
export function bandLimited(kind: 'saw' | 'square', baseHz: number, topHz: number): Wavetable {
  const out = new Float64Array(TABLE_POINTS + 1)
  const harmonics = Math.max(1, Math.floor(topHz / baseHz))
  for (let n = 1; n <= harmonics; n++) {
    // A saw is every harmonic at 1/n; a square is the odd ones at 1/n. That is the definition,
    // and building from it is both correct and the cheapest way to be sure there is nothing
    // above the limit.
    if (kind === 'square' && n % 2 === 0) continue
    const amplitude = 1 / n
    const step = (2 * Math.PI * n) / TABLE_POINTS
    const cosStep = Math.cos(step)
    const sinStep = Math.sin(step)
    let c = 1
    let s = 0
    for (let i = 0; i <= TABLE_POINTS; i++) {
      out[i]! += s * amplitude
      const next = c * cosStep - s * sinStep
      s = s * cosStep + c * sinStep
      c = next
    }
  }
  let peak = 0
  for (let i = 0; i <= TABLE_POINTS; i++) peak = Math.max(peak, Math.abs(out[i]!))
  if (peak > 0) for (let i = 0; i <= TABLE_POINTS; i++) out[i]! /= peak
  return out
}

function readTable(table: Wavetable, phase: number): number {
  const x = phase * TABLE_POINTS
  const i = x | 0
  const frac = x - i
  const a = table[i] ?? 0
  const b = table[i + 1] ?? 0
  return a + (b - a) * frac
}

/**
 * Add a wavetable voice with an attack and an exponential decay.
 *
 * The attack matters more than it looks. A note that starts at full amplitude clicks, and a
 * bass line of clicks is a bass line with a percussion part nobody wrote.
 */
export function addWave(
  out: Samples,
  sampleRate: number,
  hz: number,
  table: Wavetable,
  amplitude: number,
  attackMs: number,
  decayMs: number,
): void {
  const step = hz / sampleRate
  const attack = Math.max(1, samplesFor(attackMs, sampleRate))
  const decay = decayCoefficient(decayMs, sampleRate)
  let phase = 0
  let envelope = 0
  // The attack holds the envelope up, so the decay starts from full amplitude at `attack - 1`.
  // Computed rather than watched, for `decayCutoff`'s reason.
  const end = Math.min(out.length, attack - 1 + decayCutoff(amplitude, decayMs, sampleRate))
  for (let i = 0; i < end; i++) {
    // Linear up, exponential down: an attack is a physical rise time and a decay is a physical
    // energy loss, and they are genuinely different shapes.
    envelope = i < attack ? (amplitude * (i + 1)) / attack : envelope * decay
    out[i]! += readTable(table, phase) * envelope
    phase += step
    if (phase >= 1) phase -= 1
  }
}

/** Beats per minute of the flagship score (07 §5). */
export const SCORE_BPM = 118

/** The pitch every note buffer is rendered at, and the rate-1 reference for the sequencer. */
export const BASS_ROOT_HZ = 55 // A1
export const ARP_ROOT_HZ = 220 // A3
export const PAD_ROOT_HZ = 110 // A2
export const LEAD_ROOT_HZ = 440 // A4

/**
 * Detuning, in cents.
 *
 * 07 §5 asks for a "detuned saw" twice, and the number is what makes it a synthwave bass rather
 * than an organ: two oscillators a few cents apart beat against each other at a fraction of a
 * hertz, which the ear hears as width rather than as two notes.
 */
const DETUNE_CENTS = 11

function cents(hz: number, amount: number): number {
  return hz * Math.pow(2, amount / 1200)
}

/**
 * The bass: a detuned saw, one eighth-note long.
 *
 * The only layer that plays throughout, so it carries the tempo on its own for as long as a
 * player takes to light the first Star Core.
 */
export function renderBassNote(sampleRate: number): Samples {
  const out = new Float32Array(samplesFor(420, sampleRate))
  // 1.8 kHz, not the edge of hearing: the lowpass below throws everything above 220 Hz away, so
  // a table built to 9 kHz would be a hundred and thirty harmonics computed and then filtered
  // out. It was, and it cost seven milliseconds of the render budget for nothing audible.
  const table = bandLimited('saw', BASS_ROOT_HZ, 1800)
  addWave(out, sampleRate, cents(BASS_ROOT_HZ, -DETUNE_CENTS), table, 0.5, 4, 300)
  addWave(out, sampleRate, cents(BASS_ROOT_HZ, DETUNE_CENTS), table, 0.5, 4, 300)
  // A lowpass at four times the root: a bass with its ninth harmonic intact is a lead.
  filterInPlace(out, Biquad.lowpass(BASS_ROOT_HZ * 4, 0.9, sampleRate))
  fadeOut(out, sampleRate, 6)
  normalise(out)
  return out
}

/**
 * The arp: a square, one sixteenth long.
 *
 * Rendered dry. 07 §5 wants "resonance rising with Star Core", and a resonance baked into the
 * buffer is a resonance that cannot rise — so the filter is on the layer, at runtime, which is
 * the sanctioned exception of 07 §1 rule 5 for a continuous voice.
 */
export function renderArpNote(sampleRate: number): Samples {
  const out = new Float32Array(samplesFor(180, sampleRate))
  const table = bandLimited('square', ARP_ROOT_HZ, 9000)
  addWave(out, sampleRate, ARP_ROOT_HZ, table, 0.8, 2, 90)
  fadeOut(out, sampleRate, 4)
  normalise(out)
  return out
}

/**
 * The pad: a slow-attack detuned saw, one bar long.
 *
 * Three saws rather than two, a fifth apart, so one buffer is a chord and the sequencer plays
 * one note per bar instead of three. The attack is 380 ms, which is what makes it a pad — a
 * fast one is a stab, and 07 §5 asks for both in different places.
 */
export function renderPadChord(sampleRate: number): Samples {
  // A bar at 118 BPM is 2.03 seconds and the pad re-strikes every other bar, so this is one
  // strike with room to overlap the next rather than a note held for its own sake.
  const out = new Float32Array(samplesFor(2200, sampleRate))
  const table = bandLimited('saw', PAD_ROOT_HZ, 4200)
  for (const [ratio, gain] of [
    [1, 0.5],
    [1.4983, 0.34], // a just fifth, near enough that the beating is slow
  ] as const) {
    addWave(out, sampleRate, cents(PAD_ROOT_HZ * ratio, -DETUNE_CENTS), table, gain, 380, 2200)
    addWave(out, sampleRate, cents(PAD_ROOT_HZ * ratio, DETUNE_CENTS), table, gain, 380, 2200)
  }
  // The octave, single rather than detuned: at a fifth of the root's level the beating is
  // inaudible and the second oscillator is a sixth of the note's render cost.
  addWave(out, sampleRate, PAD_ROOT_HZ * 2, table, 0.22, 380, 2200)
  filterInPlace(out, Biquad.lowpass(1600, 0.8, sampleRate))
  fadeOut(out, sampleRate, 200)
  normalise(out)
  return out
}

/**
 * The lead: a saw with a portamento *into* the note.
 *
 * True portamento glides from whatever was playing before, which a baked buffer cannot know. A
 * fixed approach from two semitones below is what a player actually hears as portamento — the
 * arrival, not the departure — and it costs one buffer instead of one per interval.
 */
export function renderLeadNote(sampleRate: number): Samples {
  const out = new Float32Array(samplesFor(520, sampleRate))
  const table = bandLimited('saw', LEAD_ROOT_HZ, 11_000)
  const glide = samplesFor(70, sampleRate)
  const from = LEAD_ROOT_HZ * Math.pow(2, -2 / 12)
  const attack = Math.max(1, samplesFor(8, sampleRate))
  const decay = decayCoefficient(420, sampleRate)
  const ratio = Math.pow(LEAD_ROOT_HZ / from, 1 / glide)
  let hz = from
  let phase = 0
  let envelope = 0
  const end = Math.min(out.length, attack - 1 + decayCutoff(0.9, 420, sampleRate))
  for (let i = 0; i < end; i++) {
    envelope = i < attack ? (0.9 * (i + 1)) / attack : envelope * decay
    out[i]! += readTable(table, phase) * envelope
    phase += hz / sampleRate
    if (phase >= 1) phase -= 1
    if (i < glide) hz *= ratio
  }
  filterInPlace(out, Biquad.lowpass(4200, 1.1, sampleRate))
  fadeOut(out, sampleRate, 8)
  normalise(out)
  return out
}

/**
 * The kick: a sine falling from 130 Hz to 48, with a click on the front.
 *
 * The same shape as a solenoid body (07 §3.1) and for the same physical reason — something
 * heavy is being struck and the pitch of the ring falls as it settles — so it is the same
 * function. The beater click is four milliseconds of noise, and without it a kick is a note
 * rather than a hit.
 */
export function renderKick(sampleRate: number, noise: NoiseSource): Samples {
  const out = new Float32Array(samplesFor(320, sampleRate))
  addPitchDrop(out, sampleRate, 130, 48, 45, 1, 210)
  const click = samplesFor(4, sampleRate)
  for (let i = 0; i < click; i++) out[i]! += noise.next() * 0.35 * (1 - i / click)
  fadeOut(out, sampleRate, 6)
  normalise(out)
  return out
}

/** The snap snare: a short noise burst over two tuned modes, bandpassed and gated hard. */
export function renderSnapSnare(sampleRate: number, noise: NoiseSource): Samples {
  const out = new Float32Array(samplesFor(180, sampleRate))
  const decay = decayCoefficient(90, sampleRate)
  let envelope = 1
  for (let i = 0; i < out.length; i++) {
    out[i]! += noise.next() * envelope
    envelope *= decay
  }
  filterInPlace(out, Biquad.bandpass(1900, 0.8, sampleRate))
  // The shell, under the snares. Two modes, because one is a tom.
  const shell = new Float32Array(out.length)
  addWave(shell, sampleRate, 185, bandLimited('saw', 185, 3000), 0.25, 1, 70)
  addWave(shell, sampleRate, 278, bandLimited('saw', 278, 3000), 0.16, 1, 55)
  filterInPlace(shell, Biquad.lowpass(2600, 0.9, sampleRate))
  for (let i = 0; i < out.length; i++) out[i]! += shell[i]!
  fadeOut(out, sampleRate, 6)
  normalise(out)
  return out
}

/** The closed hat: highpassed noise, gated at 45 ms. */
export function renderClosedHat(sampleRate: number, noise: NoiseSource): Samples {
  const out = new Float32Array(samplesFor(70, sampleRate))
  const decay = decayCoefficient(28, sampleRate)
  let envelope = 1
  for (let i = 0; i < out.length; i++) {
    out[i]! += noise.next() * envelope
    envelope *= decay
  }
  filterInPlace(out, Biquad.highpass(7200, 0.7, sampleRate))
  fadeOut(out, sampleRate, 4)
  normalise(out)
  return out
}

// --- the score ------------------------------------------------------------------------------

/** The layers of 07 §5, in the order they enter. */
export const LAYER_NAMES = ['bass', 'arp', 'drums', 'pad', 'lead'] as const
export type LayerName = (typeof LAYER_NAMES)[number]

/** A note in a pattern: which sixteenth of the bar, and how far above the layer's root. */
export interface Step {
  /** 0…15 within the bar. */
  readonly at: number
  /** Semitones above the layer's root, before the bar's chord is added. */
  readonly semitones: number
  readonly gain: number
}

/**
 * A score.
 *
 * `chords` is one root per bar, in semitones above the key. Every layer's pitches are relative
 * to it, so the whole thing transposes by changing four numbers — which is what makes this a
 * program rather than a track.
 */
export interface Score {
  readonly name: string
  readonly bpm: number
  readonly chords: readonly number[]
  readonly bass: readonly Step[]
  readonly arp: readonly Step[]
  readonly lead: readonly Step[]
  readonly kick: readonly number[]
  readonly snare: readonly number[]
  readonly hat: readonly number[]
  /** Bars where the pad re-strikes. One per bar is a wash; every other bar breathes. */
  readonly padBars: readonly number[]
  /**
   * The mode whose start cuts the music dead (07 §5's BIG BANG blackout).
   *
   * Named by the **score** rather than by the sequencer, because which of a machine's modes is
   * its climax is a fact about that machine's music and about nothing else. Every other mode
   * start gets the ordinary intro — everything but the pad, for 2.2 seconds, under the callout.
   */
  readonly blackoutMode?: string
}

/** Root, fifth, octave — 07 §5's bass, as eighth notes. */
const BASS_FIGURE: readonly Step[] = [
  { at: 0, semitones: 0, gain: 1 },
  { at: 2, semitones: 0, gain: 0.7 },
  { at: 4, semitones: 7, gain: 0.85 },
  { at: 6, semitones: 0, gain: 0.7 },
  { at: 8, semitones: 12, gain: 0.9 },
  { at: 10, semitones: 0, gain: 0.7 },
  { at: 12, semitones: 7, gain: 0.85 },
  { at: 14, semitones: 0, gain: 0.75 },
]

/** A sixteenth-note minor arpeggio, up and back. */
const ARP_FIGURE: readonly Step[] = [0, 3, 7, 12, 15, 12, 7, 3, 0, 3, 7, 12, 15, 12, 7, 3].map(
  (semitones, at) => ({ at, semitones, gain: at % 4 === 0 ? 1 : 0.72 }),
)

/** The lead: sparse, and above the arp, so it reads as a melody rather than as another part. */
const LEAD_FIGURE: readonly Step[] = [
  { at: 0, semitones: 12, gain: 1 },
  { at: 6, semitones: 15, gain: 0.9 },
  { at: 10, semitones: 19, gain: 0.95 },
]

export const MUSIC_PROGRAMS: Readonly<Record<string, Score>> = {
  /**
   * NEON NOVA's score, named by the machine's `audio.music.program`.
   *
   * A minor, i–VI–III–VII: the four chords synthwave is made of, and the reason is that the
   * progression never resolves — it can loop for the length of a ball without arriving
   * anywhere, which is exactly what a game needs and a song does not.
   */
  salvageScore: {name:'salvageScore',bpm:102,chords:[0,5,8,7,0,10,5,7],
    bass:[{at:0,semitones:0,gain:1},{at:3,semitones:7,gain:.65},{at:8,semitones:0,gain:.85},{at:14,semitones:12,gain:.55}],
    arp:[0,7,10,14,12,7,5,10].map((semitones,i)=>({at:i*2,semitones,gain:i%2===0?.8:.55})),
    lead:[{at:0,semitones:19,gain:.75},{at:7,semitones:17,gain:.7},{at:12,semitones:14,gain:.8}],
    kick:[0,7,8],snare:[4,12],hat:[0,2,4,6,8,10,12,14],padBars:[0,2,4,6]},
  neonNovaScore: {
    name: 'neonNovaScore',
    bpm: SCORE_BPM,
    chords: [0, 8, 3, 10],
    bass: BASS_FIGURE,
    arp: ARP_FIGURE,
    lead: LEAD_FIGURE,
    kick: [0, 6, 8, 14],
    snare: [4, 12],
    hat: [0, 2, 4, 6, 8, 10, 12, 14],
    padBars: [0, 2],
    blackoutMode: 'bigBang',
  },
}

/** The program a machine gets when it names one that does not exist. Reported, never silent. */
export const DEFAULT_PROGRAM = 'neonNovaScore'
